<template>
  <RadioGroupOption as="template" :value="offerPrice.contractSku" v-slot="{ checked, active }">
    <div
      :class="[
        checked ? 'border-primary' : 'border-gray-300',
        active ? 'border-primary ring-2 ring-primary' : '',
        'relative overflow-hidden flex border bg-white p-4 shadow-xs focus:outline-hidden grow rounded-3xl cursor-pointer',
      ]"
    >
      <span class="flex flex-1">
        <span class="flex flex-col">
          <template v-if="isMpcpCategory && offerPrice.monthly">
            <RadioGroupLabel as="span" class="block text-md font-medium">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="block text-sm font-bold" :class="getMpcpOfferColor(offerPrice.offerMarketingTitle)">
                  {{ offerPrice.offerMarketingTitle }}
                </span>
                <span
                  v-if="isRecommended"
                  class="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-primary text-button-font-primary"
                >
                  Recommandé
                </span>
              </div>
              <span class="mt-1 text-gray-500 text-sm italic block">
                Jusqu'à {{ formatPrice(getMpcpMaxCoverage(offerPrice.offerMarketingTitle)) }}
              </span>
            </RadioGroupLabel>
          </template>

          <template v-else>
            <RadioGroupLabel as="span" class="block text-md font-medium text-gray-900">
              <div class="flex items-center gap-2 flex-wrap">
                {{ offerPriceDisplayTermLength }}
              </div>
            </RadioGroupLabel>
          </template>

          <RadioGroupDescription as="span" class="text-gray-500 mt-1 flex items-center text-md">
            <span v-if="monthDiscount && offerPrice.monthly" class="text-primary-dark text-sm">
              {{ discount.finalPrice }} pendant {{ discount.offeredMonths }} mois*
            </span>
            <div v-else>
              <span class="font-bold text-gray-900">{{ offerPriceDisplayPrice }}</span>
              <div
                v-if="isEligibleForReduction"
                class="w-32 absolute top-6 -right-2 transform rotate-45 bg-primary-light"
              >
                <span class="pl-[22px] text-primary-dark text-sm">Remise de {{ offerPriceComputeReduction }}%</span>
              </div>
              <span v-if="isEligibleForReduction" class="line-through text-primary font-light text-sm ml-1">
                {{ offerPriceDisplayReduction }}
              </span>
            </div>
          </RadioGroupDescription>
        </span>
      </span>

      <CheckCircleIcon :class="[!checked ? 'invisible' : '', 'h-5 w-5 text-primary-dark']" aria-hidden="true" />

      <div v-if="monthDiscount && offerPrice.monthly">
        <div class="w-32 absolute top-6 -right-2 transform rotate-45 bg-primary-light">
          <span class="pr-4 pl-8 ml-1 text-primary-dark text-sm">Promotion</span>
        </div>
      </div>

      <span
        :class="[
          active ? 'border' : 'border-2',
          checked ? 'border-primary' : 'border-transparent',
          'pointer-events-none absolute -inset-px rounded-3xl cursor-pointer',
        ]"
        aria-hidden="true"
      />
    </div>
  </RadioGroupOption>
</template>

<script>
import { CheckCircleIcon } from "@heroicons/vue/outline";
import { RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import { formatPrice, formatPriceTTC, formatDuration, getMpcpOfferColor, getMpcpMaxCoverage } from "@estaly/ui";

export default {
  components: {
    RadioGroupOption,
    RadioGroupDescription,
    RadioGroupLabel,
    CheckCircleIcon,
  },

  props: {
    offerPrice: { type: Object, required: true },
    offerPrices: { type: Array, required: true },
    recommendedOffer: { type: String, default: null },
    isMpcpCategory: { type: Boolean, default: false },
    monthDiscount: { type: Boolean, default: false },
    discount: { type: Object, default: null },
  },

  computed: {
    isRecommended() {
      return this.recommendedOffer && this.offerPrice?.offerMarketingTitle === this.recommendedOffer;
    },

    referenceOffer() {
      return this.offerPrices.find((offer) => !offer.recurring);
    },

    offerPriceDisplayTermLength() {
      if (this.offerPrice.monthly) {
        if (this.isMpcpCategory) {
          return `Jusqu'à ${formatPrice(getMpcpMaxCoverage(this.offerPrice.offerMarketingTitle))}`;
        }
        return "Paiement mensuel";
      }
      const { termLength } = this.offerPrice;
      const durationText = this.formatDuration(termLength);
      return this.offerPrice.recurring ? `${durationText} reconductible` : durationText;
    },

    offerPriceDisplayPrice() {
      const formattedPrice = formatPriceTTC(this.offerPrice.price);
      const timeUnit = this.offerPrice.monthly ? "/mois" : this.offerPrice.recurring ? "/an" : "";
      return `${formattedPrice}${timeUnit}`;
    },

    offerPriceDisplayReduction() {
      if (!this.referenceOffer) return "";
      const referencePricePerMonth = this.referenceOffer.price / this.referenceOffer.termLength;
      const totalReferencePrice = referencePricePerMonth * this.offerPrice.termLength;
      return this.offerPrice.termLength > 12 ? formatPrice(totalReferencePrice) : "";
    },

    offerPriceComputeReduction() {
      if (!this.referenceOffer) return 0;
      const referencePricePerMonth = this.referenceOffer.price / this.referenceOffer.termLength;
      const totalReferencePrice = referencePricePerMonth * this.offerPrice.termLength;
      if (totalReferencePrice <= 0) return 0;
      return Math.round(((totalReferencePrice - this.offerPrice.price) * 100) / totalReferencePrice);
    },

    isEligibleForReduction() {
      return !this.offerPrice.recurring && this.offerPrice.contractSku !== this.referenceOffer?.contractSku;
    },
  },

  methods: {
    formatPrice,
    formatDuration,
    getMpcpOfferColor,
    getMpcpMaxCoverage,
  },
};
</script>
